<script setup>
import {
    computed,
} from 'vue';
import {
    formatDate,
} from '../../../utils/filter';
import {
    trackSubContentGroup,
} from '../../../utils/tracking';

import AppApplicationActions from './AppApplicationActions';
import AppBadge from '../../atoms/app/AppBadge';
import AppIcon from '../../atoms/app/AppIcon';
import AppLink from '../../atoms/app/AppLink';
import SkeletonBox from '../../atoms/skeleton/SkeletonBox';
import TextHeadline from '../../atoms/text/TextHeadline';

import '../../../lang/application';

const props = defineProps({
    application: {
        default: null,
        type: Object,
    },
    hasRemove: {
        default: true,
        type: Boolean,
    },
});

const emit = defineEmits(['clicked', 'memo', 'remove', 'application-status']);

const isLoading = computed(() => !props.application);

function emitOpenDetail(event) {
    trackSubContentGroup('Detail');
    emit('clicked', event);
}

</script>

<template>
    <div
        class="c-appApplicationItem"
    >
        <div class="k-o-media">
            <div class="k-o-media__figure">
                <SkeletonBox
                    v-if="isLoading"
                    class="c-appApplicationItem__companyLogoSkeleton"
                    data-qa="company logo skeleton box"
                />
                <div
                    v-else
                    class="k-c-imageBox c-appApplicationItem__imageBox"
                >
                    <div class="c-appApplicationItem__companyLogo">
                        <AppIcon
                            v-if="application.job?.isChiffre"
                            name="company"
                            size="2xl"
                            data-qa="chiffre logo"
                        />
                        <img
                            v-else-if="application.company?.logo"
                            :src="application.company.logo"
                            :alt="`Logo des Arbeitgebers ${application.companyName}`"
                            data-qa="company logo"
                        >
                        <img
                            v-else
                            src="../../../assets/img/company-fallback-logo.png"
                            alt=""
                        >
                    </div>
                </div>
            </div>
            <div class="k-o-media__body">
                <div class="k-c-excerpt">
                    <div class="c-appApplicationItem__headlineWrapper k-c-excerpt__headline">
                        <SkeletonBox
                            v-if="isLoading"
                            data-qa="headline loading skeleton box"
                        />
                        <template v-else>
                            <TextHeadline
                                :level="3"
                                variant="base-700"
                                class="u-word-break"
                                data-qa="headline"
                            >
                                <AppLink
                                    :to="{
                                        name: 'page-application-detail',
                                        params: { id: application.id },
                                        query: {
                                            page: $route.query.page,
                                            filter: $route.query.filter,
                                        }
                                    }"
                                    variant="gray-no-underline"
                                    class="c-appApplicationItem__link"
                                    data-gtm-element="AP_L: Application"
                                    data-gtm-element-detail="Open Detail"
                                    @click="emitOpenDetail"
                                >
                                    {{ application.jobTitle }}
                                </AppLink>
                            </TextHeadline>
                            <AppApplicationActions
                                :application="application"
                                :has-remove="hasRemove"
                                ga-prefix="AP_L"
                                @memo="emit('memo')"
                                @remove="emit('remove')"
                            />
                        </template>
                    </div>
                    <div
                        class="k-c-excerpt__snippet c-appApplicationItem__snippet"
                        data-qa="snippet"
                    >
                        <SkeletonBox
                            v-if="isLoading"
                            data-qa="snippet loading skeleton box"
                        />
                        <div
                            v-else
                            class="u-ellipsis"
                        >
                            <span class="k-o-chain__link">
                                {{ application.companyName }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="c-appApplicationItem__metaEnd">
            <SkeletonBox
                v-if="isLoading"
                data-qa="meta end skeleton box"
            />
            <template v-else>
                <span data-qa="date">
                    Beworben:
                    {{ formatDate({ format: 'd.m.Y', date: application.createDate }) }}
                </span>
                <AppBadge
                    v-if="application.status"
                    class="c-appApplicationItem__status"
                    :dark="application.isRejected"
                    data-qa="status"
                >
                    <template v-if="application.isRejected">
                        {{ $t(`application.status.rejected`) }}
                    </template>
                    <template v-else>
                        {{ $t(`application.status.${application.status.toLowerCase()}`) }}
                    </template>
                </AppBadge>
                <AppIcon
                    v-if="application.memo"
                    name="notepad"
                    size="s"
                    fixed-width
                />
            </template>
        </div>
    </div>
</template>

<style lang="scss">
@import '~@karriereat/global-styles/scss/tools/typo';
@import '../../../assets/scss/settings/breakpoint';
@import '../../../assets/scss/settings/spacing';
@import '../../../assets/scss/utilities/anchor-block-overlay';

// 1. Trump skeleton inline styles.
// 2. Make the skeleton the same size as the regular logo.
// 3. Increase specificity to override styles from Global-Styles (this component should be refactored in the future
//    so that this is no longer necessary)
.c-appApplicationItem {
    $root: &;
    $logo-height: 51px;
    $marker-width: 3px;

    position: relative;
    -webkit-tap-highlight-color: transparent;

    &__link {
        @include anchor-block-overlay;
    }

    &__companyLogo,
    &__companyLogoSkeleton {
        max-width: 14vw;
        max-height: 14vw;
        width: $logo-height !important; // 1
        height: $logo-height !important; // 1
    }

    &__companyLogo {
        display: flex;
        justify-content: center;
        align-items: center;
        color: var(--k-gray-300);
    }

    &__companyLogoSkeleton {
        box-sizing: content-box; // 2
        padding: $k-spacing--xs; // 2
    }

    &__headlineWrapper {
        display: flex;
        justify-content: space-between;
    }

    &__metaEnd {
        display: flex;
        align-items: center;
        margin-top: $k-spacing--m;
        color: var(--k-gray-600);
    }

    &__status {
        margin-right: $k-spacing--s;
        margin-left: $k-spacing--s;
    }

    .k-c-imageBox { // 3
        &#{$root}__imageBox {
            display: flex;
            justify-content: center;
            align-items: center;
            width: 50px;
            height: 50px;
            border-color: var(--k-gray-200);
            border-radius: var(--k-rounded-sm);
        }
    }

    .k-c-excerpt { // 3
        #{$root}__snippet {
            color: var(--k-gray-600);
        }
    }
}
</style>
